import { Framework } from '@/framework/instance';
import { type EditingRange, type ILayerProps, type IPoint } from '@/version-types';
import { EDIT_AREA_ID_SELECTOR, TEXT_EDIT_EVENTS } from './constants';
import { getTextEditState } from './states/text-edit-state';
import { RangeHelper } from './utils';

export { TEXT_EDIT_EVENTS } from './constants';

export interface ITextEditPlugin {
    /**
     * 获取当前编辑的图层
     */
    getEditingLayer(): ILayerProps | null;

    /**
     * 获取选中区域状态
     */
    getEditingRange(): EditingRange | null;

    /**
     * 设置选中区域状态
     * @param range - 选中区域状态
     */
    setEditingRange(range: EditingRange | null): void;

    /**
     * 进入编辑
     * ATTENTION: 注意该方法为异步
     * @param layerID - 图层 ID
     */
    enterEditing(layerID: string, point?: IPoint): Promise<void>;

    /**
     * 退出编辑
     * ATTENTION: 注意该方法为异步
     */
    exitEditing(): Promise<void>;

    /**
     * 保持当前选区
     * @returns 解锁函数
     */
    lockEditingRange(): () => void;

    /**
     * 监听当前选区变化
     * @param fn - 事件回调
     * @returns - 监听销毁函数
     */
    onEditingRangeChange(fn: (range: EditingRange | null) => void): () => void;

    /**
     * 监听鼠标按下事件
     * @param fn - 事件回调
     * @returns - 监听销毁函数
     */
    onMouseDown(fn: (event: MouseEvent) => void): () => void;

    /**
     * 监听进入编辑态事件
     * @param fn - 事件回调
     * @returns - 监听销毁函数
     */
    onBeforeEnterEditing(fn: (layer: ILayerProps) => void): () => void;
}

export class TextEditPlugin extends Framework implements ITextEditPlugin {
    getEditingLayer() {
        return getTextEditState(this.identity).editingLayer;
    }

    getEditingRange() {
        return getTextEditState(this.identity).editingRange;
    }

    setEditingRange(range: EditingRange | null) {
        getTextEditState(this.identity).setEditingRange(range);
    }

    enterEditing(layerID: string, point?: IPoint) {
        return getTextEditState(this.identity).enterEditing(layerID, point);
    }

    exitEditing() {
        return getTextEditState(this.identity).exitEditing();
    }

    lockEditingRange() {
        const noop = () => {};

        const editingRange = getTextEditState(this.identity).editingRange;
        if (!editingRange || editingRange.isCollapsed) return noop;

        const editArea = document.getElementById(EDIT_AREA_ID_SELECTOR);
        if (!editArea) return noop;

        getTextEditState(this.identity).preventDefaultSelectEvent();

        const tid = setTimeout(() => {
            RangeHelper.applyEditingRange(editArea, editingRange);
        }, 0);

        return () => {
            clearTimeout(tid);
            getTextEditState(this.identity).allowDefaultSelectEvent();
        };
    }

    onEditingRangeChange(fn: (range: EditingRange | null) => void) {
        const id = this.framework.events.on(TEXT_EDIT_EVENTS.RANGE_CHANGE, fn);
        return () => this.framework.events.off(id);
    }

    onMouseDown(fn: (event: MouseEvent) => void) {
        const id = this.framework.events.on(TEXT_EDIT_EVENTS.MOUSE_DOWN, fn);
        return () => this.framework.events.off(id);
    }

    onBeforeEnterEditing(fn: (layer: ILayerProps) => void) {
        const id = this.framework.events.on(TEXT_EDIT_EVENTS.BEFORE_ENTER_EDITING, fn);
        return () => this.framework.events.off(id);
    }
}
